import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TestPlan, Session, Milestone } from '../../types';
import { apiRequest } from '../../utils/api';
import { theme } from '../../theme';
import { formatDate } from '../../utils/date';
import { useToast, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';

interface MilestoneSelectorProps {
  value: string;
  onChange: (milestone: string) => void;
  productId?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

const MilestoneSelector: React.FC<MilestoneSelectorProps> = ({
  value,
  onChange,
  productId,
  placeholder = 'e.g., Q1 2024, Sprint 5, Release 2.0',
  required = false,
  disabled = false
}) => {
  const [milestoneDropdownOpen, setMilestoneDropdownOpen] = useState(false);
  const [milestoneSearchValue, setMilestoneSearchValue] = useState<string>('');
  const [milestoneDropdownPosition, setMilestoneDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [allMilestones, setAllMilestones] = useState<string[]>([]);
  const [milestoneMetadata, setMilestoneMetadata] = useState<Record<string, Milestone>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<string | null>(null);
  const [newMilestoneName, setNewMilestoneName] = useState<string>(''); // Store milestone name separately for create modal
  const [newMilestoneStartDate, setNewMilestoneStartDate] = useState<string>('');
  const [newMilestoneEndDate, setNewMilestoneEndDate] = useState<string>('');
  const [editMilestoneStartDate, setEditMilestoneStartDate] = useState<string>('');
  const [editMilestoneEndDate, setEditMilestoneEndDate] = useState<string>('');
  const milestoneDropdownRef = useRef<HTMLDivElement>(null);
  const milestoneButtonRef = useRef<HTMLDivElement>(null);
  const milestoneInputRef = useRef<HTMLInputElement>(null);
  const { message, setMessage } = useToast();

  // Fetch milestones from API and also from test plans/sessions as fallback
  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        // Fetch milestones from API (with metadata)
        const apiUrl = productId ? `/api/milestones?productId=${encodeURIComponent(productId)}` : '/api/milestones';
        const apiMilestones = await apiRequest<Milestone[]>(apiUrl, {}, { useAuth: true })
          .catch(() => []); // Fallback if API fails
        
        const milestoneMap: Record<string, Milestone> = {};
        const milestoneSet = new Set<string>();
        
        // Add milestones from API
        (apiMilestones || []).forEach(m => {
          milestoneMap[m.name] = m;
          milestoneSet.add(m.name);
        });
        
        // Also fetch from test plans and sessions as fallback (for backwards compatibility)
        try {
          const [testPlans, sessions] = await Promise.all([
            apiRequest<TestPlan[]>('/api/testplans', {}, { useAuth: true }).catch(() => []),
            apiRequest<Session[]>('/api/sessions', {}, { useAuth: true }).catch(() => [])
          ]);
          
          // Add milestones from test plans
          (testPlans || []).forEach(tp => {
            if (productId && tp.productId !== productId) return;
            if (tp.milestone && tp.milestone.trim()) {
              const name = tp.milestone.trim();
              milestoneSet.add(name);
              // Only add to metadata if not already there
              if (!milestoneMap[name]) {
                milestoneMap[name] = {
                  name,
                  productId: tp.productId || '',
                  startDate: new Date().toISOString(), // Default to now
                  createdAt: new Date().toISOString()
                };
              }
            }
          });
          
          // Add milestones from sessions
          (sessions || []).forEach(session => {
            if (productId && session.productId !== productId) return;
            if (session.milestone && session.milestone.trim()) {
              const name = session.milestone.trim();
              milestoneSet.add(name);
              // Only add to metadata if not already there
              if (!milestoneMap[name]) {
                milestoneMap[name] = {
                  name,
                  productId: session.productId || '',
                  startDate: new Date().toISOString(), // Default to now
                  createdAt: new Date().toISOString()
                };
              }
            }
          });
        } catch (error) {
          console.warn('Error fetching milestones from test plans/sessions:', error);
        }
        
        setAllMilestones(Array.from(milestoneSet).sort());
        setMilestoneMetadata(milestoneMap);
      } catch (error) {
        console.error('Error fetching milestones:', error);
        setAllMilestones([]);
        setMilestoneMetadata({});
      }
    };
    fetchMilestones();
  }, [productId]);

  // Calculate dropdown position when it opens or window resizes
  const updateDropdownPosition = useCallback(() => {
    if (milestoneDropdownOpen && milestoneButtonRef.current) {
      const buttonElement = milestoneButtonRef.current;
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 300; // Max height of dropdown
        const dropdownWidth = Math.min(400, viewportWidth - 16); // Max width, with margin
        
        // Open upward if there's not enough space below but enough space above
        const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        
        // Calculate top position (getBoundingClientRect is relative to viewport, perfect for fixed positioning)
        let top = openUpward 
          ? rect.top - dropdownHeight - 4
          : rect.bottom + 4;
        
        // Ensure dropdown stays within viewport
        if (top < 4) {
          top = 4; // Small margin from top
        } else if (top + dropdownHeight > viewportHeight - 4) {
          top = viewportHeight - dropdownHeight - 4; // Small margin from bottom
        }
        
        // Calculate left position, ensure it doesn't overflow right edge
        let left = rect.left;
        const preferredWidth = Math.min(rect.width, dropdownWidth);
        if (left + preferredWidth > viewportWidth - 4) {
          left = viewportWidth - preferredWidth - 4; // Align to right edge with margin
        }
        if (left < 4) {
          left = 4; // Small margin from left
        }
        
        // For fixed positioning, use viewport coordinates directly (no scroll offset needed)
        setMilestoneDropdownPosition({
          top,
          left
        });
      }
    }
  }, [milestoneDropdownOpen]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (milestoneDropdownOpen) {
      updateDropdownPosition();
    } else {
      setMilestoneDropdownPosition(null);
    }
  }, [milestoneDropdownOpen, updateDropdownPosition]);

  // Update position on window resize
  useEffect(() => {
    if (!milestoneDropdownOpen) return;
    
    const handleResize = () => {
      updateDropdownPosition();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [milestoneDropdownOpen, updateDropdownPosition]);

  // Close dropdown on scroll (prevents it from appearing detached)
  useEffect(() => {
    if (!milestoneDropdownOpen) return;
    
    const handleScroll = () => {
      setMilestoneDropdownOpen(false);
      setMilestoneSearchValue('');
    };
    
    // Listen to scroll on window and all scrollable parents
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scrolls
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [milestoneDropdownOpen]);

  // Close milestone dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (milestoneDropdownRef.current && !milestoneDropdownRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!milestoneButtonRef.current?.contains(target)) {
          setMilestoneDropdownOpen(false);
          setMilestoneSearchValue('');
        }
      }
    };

    if (milestoneDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [milestoneDropdownOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (milestoneDropdownOpen && milestoneInputRef.current) {
      setTimeout(() => {
        milestoneInputRef.current?.focus();
      }, 0);
    }
  }, [milestoneDropdownOpen]);

  const handleSelectMilestone = (milestone: string) => {
    onChange(milestone);
    setMilestoneDropdownOpen(false);
    setMilestoneSearchValue('');
  };

  const handleCreateMilestone = () => {
    if (milestoneSearchValue.trim()) {
      // Store the milestone name separately so it doesn't get cleared
      setNewMilestoneName(milestoneSearchValue.trim());
      // Open modal to get start/end dates
      setNewMilestoneStartDate(new Date().toISOString().split('T')[0]); // Default to today
      setNewMilestoneEndDate('');
      setShowCreateModal(true);
    }
  };

  const handleConfirmCreateMilestone = async () => {
    if (!newMilestoneName.trim() || !newMilestoneStartDate) {
      return;
    }

    try {
      const startDate = new Date(newMilestoneStartDate).toISOString();
      const endDate = newMilestoneEndDate ? new Date(newMilestoneEndDate).toISOString() : undefined;

      await apiRequest<Milestone>('/api/milestones', {
        method: 'POST',
        body: JSON.stringify({
          name: newMilestoneName.trim(),
          productId: productId || null,
          startDate,
          endDate: endDate || null
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });

      // Refresh milestones
      const apiUrl = productId ? `/api/milestones?productId=${encodeURIComponent(productId)}` : '/api/milestones';
      const apiMilestones = await apiRequest<Milestone[]>(apiUrl, {}, { useAuth: true });
      const milestoneMap: Record<string, Milestone> = {};
      (apiMilestones || []).forEach(m => {
        milestoneMap[m.name] = m;
      });
      setMilestoneMetadata(milestoneMap);
      setAllMilestones(prev => {
        const updated = new Set([...prev, newMilestoneName.trim()]);
        return Array.from(updated).sort();
      });

      // Select the new milestone
      onChange(newMilestoneName.trim());
      setMilestoneDropdownOpen(false);
      setMilestoneSearchValue('');
      setShowCreateModal(false);
      setNewMilestoneName('');
      setNewMilestoneStartDate('');
      setNewMilestoneEndDate('');
      showTempMessage(setMessage, { type: 'success', text: 'Milestone created successfully' });
    } catch (error: any) {
      console.error('Error creating milestone:', error);
      const errorMsg = error?.message || error?.error || 'Failed to create milestone';
      showTempMessage(setMessage, { type: 'error', text: errorMsg });
    }
  };

  const handleEditMilestone = (milestoneName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const metadata = milestoneMetadata[milestoneName];
    setEditingMilestone(milestoneName);
    if (metadata) {
      setEditMilestoneStartDate(metadata.startDate ? metadata.startDate.split('T')[0] : new Date().toISOString().split('T')[0]);
      setEditMilestoneEndDate(metadata.endDate ? metadata.endDate.split('T')[0] : '');
    } else {
      setEditMilestoneStartDate(new Date().toISOString().split('T')[0]);
      setEditMilestoneEndDate('');
    }
    setShowEditModal(true);
  };

  const handleConfirmEditMilestone = async () => {
    if (!editingMilestone || !editMilestoneStartDate) {
      return;
    }

    try {
      const startDate = new Date(editMilestoneStartDate).toISOString();
      const endDate = editMilestoneEndDate ? new Date(editMilestoneEndDate).toISOString() : null;

      // Try to update first - if it fails with 404, create it instead
      try {
        await apiRequest<Milestone>(`/api/milestones/${encodeURIComponent(editingMilestone)}`, {
          method: 'PUT',
          body: JSON.stringify({
            startDate,
            endDate
          }),
          headers: { 'Content-Type': 'application/json' }
        }, { useAuth: true });
        showTempMessage(setMessage, { type: 'success', text: 'Milestone dates updated successfully' });
      } catch (updateError: any) {
        // If update fails with 404, the milestone doesn't exist - create it
        if (updateError?.status === 404 || updateError?.message?.includes('not found') || updateError?.error?.includes('not found')) {
          await apiRequest<Milestone>('/api/milestones', {
            method: 'POST',
            body: JSON.stringify({
              name: editingMilestone,
              productId: productId || null,
              startDate,
              endDate: endDate || null
            }),
            headers: { 'Content-Type': 'application/json' }
          }, { useAuth: true });
          showTempMessage(setMessage, { type: 'success', text: 'Milestone created and dates saved successfully' });
        } else {
          // Re-throw if it's a different error
          throw updateError;
        }
      }

      // Refresh milestones
      const apiUrl = productId ? `/api/milestones?productId=${encodeURIComponent(productId)}` : '/api/milestones';
      const apiMilestones = await apiRequest<Milestone[]>(apiUrl, {}, { useAuth: true });
      const milestoneMap: Record<string, Milestone> = {};
      (apiMilestones || []).forEach(m => {
        milestoneMap[m.name] = m;
      });
      setMilestoneMetadata(milestoneMap);

      setShowEditModal(false);
      setEditingMilestone(null);
      setEditMilestoneStartDate('');
      setEditMilestoneEndDate('');
    } catch (error: any) {
      console.error('Error updating milestone:', error);
      const errorMsg = error?.message || error?.error || 'Failed to update milestone';
      showTempMessage(setMessage, { type: 'error', text: errorMsg });
    }
  };

  const searchLower = milestoneSearchValue.toLowerCase().trim();
  const filteredMilestones = searchLower
    ? allMilestones.filter((m: string) => m.toLowerCase().includes(searchLower))
    : allMilestones;
  const exactMatch = searchLower && allMilestones.some((m: string) => m.toLowerCase() === searchLower);
  const showCreateOption = searchLower && !exactMatch && searchLower.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Toast message={message} />
      <div
        ref={milestoneButtonRef}
        onClick={(e) => {
          if (!disabled) {
            e.stopPropagation();
            setMilestoneDropdownOpen(!milestoneDropdownOpen);
            setMilestoneSearchValue('');
          }
        }}
        style={{
          width: '100%',
          padding: '0.5rem',
          fontSize: '0.875rem',
          border: `1px solid ${theme.gridLine}`,
          borderRadius: '4px',
          backgroundColor: disabled ? theme.surfaceElevated : theme.surface,
          color: theme.textPrimary,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ color: value ? theme.textPrimary : theme.textSecondary }}>
          {value ? (() => {
            const metadata = milestoneMetadata[value];
            if (metadata) {
              const startDate = formatDate(metadata.startDate, 'MMM dd, yyyy');
              const endDate = metadata.endDate ? formatDate(metadata.endDate, 'MMM dd, yyyy') : null;
              const dateRange = endDate ? `${startDate} - ${endDate}` : `Started: ${startDate}`;
              return `${value} (${dateRange})`;
            }
            return value;
          })() : placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transition: 'transform 0.2s',
            transform: milestoneDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}
        >
          <path
            d="M8 11L3 6h10l-5 5z"
            fill={theme.cyanGlow}
          />
        </svg>
      </div>
      {milestoneDropdownOpen && milestoneDropdownPosition && createPortal(
        <div
          ref={milestoneDropdownRef}
          style={{
            position: 'fixed',
            top: `${milestoneDropdownPosition.top}px`,
            left: `${milestoneDropdownPosition.left}px`,
            backgroundColor: theme.surface,
            border: `1px solid ${theme.gridLine}`,
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 20001, // Higher than modals (which use 1000-2000)
            minWidth: '250px',
            maxWidth: '400px',
            overflow: 'hidden',
            maxHeight: '300px', // Ensure dropdown doesn't exceed viewport
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search/Create Input */}
          <div style={{ padding: '0.5rem', borderBottom: `1px solid ${theme.gridLine}` }}>
            <input
              ref={milestoneInputRef}
              type="text"
              value={milestoneSearchValue}
              onChange={(e) => setMilestoneSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (showCreateOption) {
                    handleCreateMilestone();
                  } else if (filteredMilestones.length === 1) {
                    handleSelectMilestone(filteredMilestones[0]);
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setMilestoneDropdownOpen(false);
                  setMilestoneSearchValue('');
                }
              }}
              placeholder="Search or create milestone..."
              style={{
                width: '100%',
                padding: '0.5rem',
                fontSize: '0.875rem',
                border: `1px solid ${theme.gridLine}`,
                borderRadius: '4px',
                backgroundColor: theme.surface,
                color: theme.textPrimary,
                outline: 'none',
              }}
            />
          </div>
          {/* Milestone List */}
          <div style={{ 
            maxHeight: '250px', 
            overflowY: 'auto', 
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
          }}>
            {filteredMilestones.length === 0 && !showCreateOption ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: theme.textSecondary, fontSize: '0.875rem' }}>
                {searchLower ? 'No milestones found' : 'No milestones yet'}
              </div>
            ) : (
              <>
                {filteredMilestones.map((milestone: string) => {
                  const metadata = milestoneMetadata[milestone];
                  const startDate = metadata?.startDate ? formatDate(metadata.startDate, 'MMM dd, yyyy') : null;
                  const endDate = metadata?.endDate ? formatDate(metadata.endDate, 'MMM dd, yyyy') : null;
                  const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : startDate ? `Started: ${startDate}` : null;
                  
                  return (
                    <button
                      key={milestone}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectMilestone(milestone);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        textAlign: 'left',
                        border: 'none',
                        background: value === milestone ? theme.cyanTransparent : 'transparent',
                        color: theme.textPrimary,
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: value === milestone ? 600 : 400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '0.25rem',
                        transition: 'all 0.2s ease',
                        borderRadius: '2px',
                        margin: '0.125rem 0.25rem',
                      }}
                      onMouseEnter={(e) => {
                        if (value !== milestone) {
                          e.currentTarget.style.backgroundColor = theme.surfaceElevated;
                          e.currentTarget.style.transform = 'translateX(4px)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        } else {
                          e.currentTarget.style.backgroundColor = theme.cyanTransparent;
                          e.currentTarget.style.transform = 'translateX(2px)';
                          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 212, 255, 0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = value === milestone ? theme.cyanTransparent : 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                        <span>{milestone}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            onClick={(e) => handleEditMilestone(milestone, e)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.75rem',
                              border: `1px solid ${theme.gridLine}`,
                              borderRadius: '4px',
                              backgroundColor: theme.surface,
                              color: theme.textPrimary,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                            title="Edit milestone dates"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme.surfaceElevated;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = theme.surface;
                            }}
                          >
                            <span>✏️</span>
                          </button>
                          {value === milestone && (
                            <span style={{ fontSize: '0.875rem', color: theme.cyanGlow }}>✓</span>
                          )}
                        </div>
                      </div>
                      {dateRange && (
                        <span style={{ fontSize: '0.75rem', color: theme.textSecondary, marginTop: '0.125rem' }}>
                          {dateRange}
                        </span>
                      )}
                    </button>
                  );
                })}
                {showCreateOption && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateMilestone();
                    }}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem',
                      textAlign: 'left',
                      border: 'none',
                      borderTop: `1px solid ${theme.gridLine}`,
                      background: 'transparent',
                      color: theme.cyanGlow,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s ease',
                      borderRadius: '2px',
                      margin: '0.125rem 0.25rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.cyanTransparent;
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 212, 255, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <span>+ Create "{milestoneSearchValue}"</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Create Milestone Modal */}
      {showCreateModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => {
            setShowCreateModal(false);
            setNewMilestoneName('');
            setNewMilestoneStartDate('');
            setNewMilestoneEndDate('');
          }}
        >
          <div
            style={{
              backgroundColor: theme.surface,
              border: `1px solid ${theme.gridLine}`,
              borderRadius: '8px',
              padding: '1.5rem',
              minWidth: '400px',
              maxWidth: '500px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', color: theme.textPrimary }}>Create Milestone</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                Milestone Name <span style={{ color: theme.cyanGlow }}>*</span>
              </label>
              <input
                type="text"
                value={newMilestoneName}
                readOnly
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surfaceElevated,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                Start Date <span style={{ color: theme.cyanGlow }}>*</span>
              </label>
              <input
                type="date"
                value={newMilestoneStartDate}
                onChange={(e) => setNewMilestoneStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                End Date (Optional)
              </label>
              <input
                type="date"
                value={newMilestoneEndDate}
                onChange={(e) => setNewMilestoneEndDate(e.target.value)}
                min={newMilestoneStartDate}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewMilestoneStartDate('');
                  setNewMilestoneEndDate('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCreateMilestone}
                disabled={!newMilestoneStartDate}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: !newMilestoneStartDate ? theme.gridLine : theme.cyanGlow,
                  color: theme.surface,
                  cursor: !newMilestoneStartDate ? 'not-allowed' : 'pointer',
                  opacity: !newMilestoneStartDate ? 0.5 : 1
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Milestone Modal */}
      {showEditModal && editingMilestone && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => {
            setShowEditModal(false);
            setEditingMilestone(null);
            setEditMilestoneStartDate('');
            setEditMilestoneEndDate('');
          }}
        >
          <div
            style={{
              backgroundColor: theme.surface,
              border: `1px solid ${theme.gridLine}`,
              borderRadius: '8px',
              padding: '1.5rem',
              minWidth: '400px',
              maxWidth: '500px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', color: theme.textPrimary }}>Edit Milestone Dates</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                Milestone Name
              </label>
              <input
                type="text"
                value={editingMilestone}
                readOnly
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surfaceElevated,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                Start Date <span style={{ color: theme.cyanGlow }}>*</span>
              </label>
              <input
                type="date"
                value={editMilestoneStartDate}
                onChange={(e) => setEditMilestoneStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textPrimary, fontSize: '0.875rem' }}>
                End Date (Optional)
              </label>
              <input
                type="date"
                value={editMilestoneEndDate}
                onChange={(e) => setEditMilestoneEndDate(e.target.value)}
                min={editMilestoneStartDate}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingMilestone(null);
                  setEditMilestoneStartDate('');
                  setEditMilestoneEndDate('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${theme.gridLine}`,
                  borderRadius: '4px',
                  backgroundColor: theme.surface,
                  color: theme.textPrimary,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmEditMilestone}
                disabled={!editMilestoneStartDate}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: !editMilestoneStartDate ? theme.gridLine : theme.cyanGlow,
                  color: theme.surface,
                  cursor: !editMilestoneStartDate ? 'not-allowed' : 'pointer',
                  opacity: !editMilestoneStartDate ? 0.5 : 1
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MilestoneSelector;

